import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import {
  getLearningLabPlaybook,
  type LearningLabPlaybook,
} from '../../../../shared/onboarding/learningLabPlaybooks';
import type { AdvanceGate, MissionStep } from '../../../../shared/onboarding/missionTypes';
import {
  clearMissionSession,
  loadMissionSession,
  saveMissionSession,
  setCreateMissionDraftReady,
} from '../../../../shared/onboarding/missionSessionStorage';
import { subscribeAdvanceGate, shouldShowMissionManualNext } from './missionOrchestrator';
import type { ChecklistRow } from './MissionChecklist';
import type { TourStep } from '../../../types/onboarding';
import { trackEvent } from '../../../utils/analytics';
import { ONBOARDING_EVENTS } from '../../onboarding/onboardingEventNames';
import { TOUR_DOM_EVENTS } from '../../onboarding/onboardingDomEvents';
import {
  executeMissionFallback,
  getMissionFallbackForAnchor,
} from '../../../../shared/onboarding/missionFallbackRegistry';
import type { CoordinatorLabTourSlug } from '../../../../shared/onboarding/coordinatorLearningLabTours';
import {
  isMissionRouteSatisfied,
  resolveMissionRouteContract,
} from '../../../../shared/onboarding/missionRouteContract';
import { ensureMissionRouteForPlaybookStep } from '../../../utils/missionRouteEnsure';
type MissionAdvanceHandlers = {
  onStepComplete: (stepIndex: number, step: TourStep) => void;
  onComplete: () => void;
  setStepIndex: (index: number) => void;
  refreshTarget: () => void;
};

interface LearningLabMissionContextValue {
  playbook: LearningLabPlaybook | null;
  currentMissionStep: MissionStep | null;
  checklistRows: ChecklistRow[];
  completedIds: Set<string>;
  panelCollapsed: boolean;
  targetOccluded: boolean;
  targetReady: boolean;
  showManualNext: boolean;
  routeReady: boolean;
  routeNavigating: boolean;
  showFallbackAction: boolean;
  fallbackLabel?: string;
  setPanelCollapsed: (collapsed: boolean) => void;
  setTargetVisibility: (state: 'ready' | 'missing' | 'occluded') => void;
  registerAdvanceHandlers: (handlers: MissionAdvanceHandlers | null) => void;
  advanceMissionStep: () => void;
  skipOptionalStep: () => void;
  retryCurrentTarget: () => void;
  executeFallback: () => void;
  resumeFromSession: () => number | null;
}

const LearningLabMissionContext =
  createContext<LearningLabMissionContextValue | null>(null);

export function useLearningLabMission(): LearningLabMissionContextValue {
  const ctx = useContext(LearningLabMissionContext);
  if (!ctx) {
    throw new Error('useLearningLabMission requires LearningLabMissionProvider');
  }
  return ctx;
}

export function useLearningLabMissionOptional():
  | LearningLabMissionContextValue
  | null {
  return useContext(LearningLabMissionContext);
}

function missionStepFromTour(step: TourStep | undefined): MissionStep | null {
  if (!step?.metadata?.missionStepId) {
    return null;
  }
  const gate = step.metadata.advanceGate as AdvanceGate | undefined;
  if (!gate) {
    return null;
  }
  return {
    id: String(step.metadata.missionStepId),
    stepName: step.stepName ?? '',
    kind: (step.metadata.missionKind as MissionStep['kind']) ?? 'spotlight',
    groupId: String(step.metadata.missionGroupId ?? 'start'),
    title: String(step.title ?? ''),
    instruction: String(step.metadata.instruction ?? step.content ?? ''),
    why: step.metadata.why as string | undefined,
    tip: step.tip,
    optional: step.metadata.optional === true,
    advanceGate: gate,
    tourAnchor:
      typeof step.metadata.tourAnchor === 'string'
        ? step.metadata.tourAnchor
        : undefined,
    targetZone: step.metadata.targetZone as MissionStep['targetZone'],
    panelFallback: step.metadata.panelFallback as MissionStep['panelFallback'],
    coordinatorTabSlug:
      typeof step.metadata.coordinatorTabSlug === 'string'
        ? step.metadata.coordinatorTabSlug
        : undefined,
    module: step.metadata.module as MissionStep['module'],
    metadata: step.metadata,
    placement: step.placement,
  };
}

export const LearningLabMissionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { state } = useOnboarding();
  const location = useLocation();
  const handlersRef = useRef<MissionAdvanceHandlers | null>(null);
  const [routeNavigating, setRouteNavigating] = useState(false);
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [targetOccluded, setTargetOccluded] = useState(false);
  const [targetVisibility, setTargetVisibilityState] = useState<
    'ready' | 'missing' | 'occluded'
  >('missing');
  const gateCleanupRef = useRef<(() => void) | null>(null);
  const stepStartedAtRef = useRef<number>(Date.now());
  const missionStartedSlugRef = useRef<string | null>(null);
  const hydratedMissionSlugRef = useRef<string | null>(null);

  const playbook = useMemo(() => {
    if (!state.missionMode || !state.currentFlowId) {
      return null;
    }
    return getLearningLabPlaybook(state.currentFlowId) ?? null;
  }, [state.currentFlowId, state.missionMode]);

  const currentTourStep = state.currentSteps[state.currentStepIndex];
  const currentMissionStep = missionStepFromTour(currentTourStep);

  const playbookStep = useMemo(() => {
    if (!playbook || !currentMissionStep) {
      return null;
    }
    return playbook.steps.find((s) => s.id === currentMissionStep.id) ?? null;
  }, [currentMissionStep, playbook]);

  const routeContract = useMemo(() => {
    if (!playbookStep || !playbook) {
      return null;
    }
    return resolveMissionRouteContract(
      playbookStep,
      playbook.slug as CoordinatorLabTourSlug,
    );
  }, [playbook, playbookStep]);

  const routeReady = useMemo(() => {
    if (!routeContract) {
      return true;
    }
    return isMissionRouteSatisfied(routeContract, {
      pathname: location.pathname,
      search: location.search,
    });
  }, [location.pathname, location.search, routeContract]);

  const registerAdvanceHandlers = useCallback(
    (handlers: MissionAdvanceHandlers | null) => {
      handlersRef.current = handlers;
    },
    [],
  );

  const persistSession = useCallback(
    (stepIndex: number, completed: Set<string>, stepId?: string) => {
      if (!user?.id || !state.currentFlowId) {
        return;
      }
      saveMissionSession(user.id, state.currentFlowId, {
        stepIndex,
        stepId,
        completedIds: [...completed],
        startedAt: stepStartedAtRef.current,
      });
    },
    [state.currentFlowId, user?.id],
  );

  const advanceMissionStep = useCallback(() => {
    if (!routeReady || routeNavigating) {
      return;
    }
    const handlers = handlersRef.current;
    const step = state.currentSteps[state.currentStepIndex];
    if (!handlers || !step) {
      return;
    }

    const isLast = state.currentStepIndex >= state.currentSteps.length - 1;

    if (!currentMissionStep) {
      handlers.onStepComplete(state.currentStepIndex, step);
      if (isLast) {
        handlers.onComplete();
        return;
      }
      const nextIndex = state.currentStepIndex + 1;
      const nextStep = state.currentSteps[nextIndex];
      const nextStepId =
        typeof nextStep?.metadata?.missionStepId === 'string'
          ? nextStep.metadata.missionStepId
          : undefined;
      handlers.setStepIndex(nextIndex);
      persistSession(nextIndex, completedIds, nextStepId);
      stepStartedAtRef.current = Date.now();
      return;
    }

    const nextCompleted = new Set(completedIds);
    nextCompleted.add(currentMissionStep.id);
    setCompletedIds(nextCompleted);

    trackEvent({
      action: ONBOARDING_EVENTS.LEARNING_LAB_MISSION_STEP_COMPLETED,
      category: 'onboarding',
      label: currentMissionStep.id,
      custom_parameters: {
        flow_id: state.currentFlowId,
        gate_type: currentMissionStep.advanceGate.type,
        step_id: currentMissionStep.id,
        learning_lab_active: true,
      },
    });

    handlers.onStepComplete(state.currentStepIndex, step);

    if (isLast) {
      trackEvent({
        action: ONBOARDING_EVENTS.LEARNING_LAB_MISSION_COMPLETED,
        category: 'onboarding',
        label: state.currentFlowId,
        custom_parameters: { learning_lab_active: true },
      });
      if (user?.id && state.currentFlowId) {
        clearMissionSession(user.id, state.currentFlowId);
      }
      setCreateMissionDraftReady(false);
      handlers.onComplete();
      return;
    }

    const nextIndex = state.currentStepIndex + 1;
    const nextStep = state.currentSteps[nextIndex];
    const nextStepId =
      typeof nextStep?.metadata?.missionStepId === 'string'
        ? nextStep.metadata.missionStepId
        : undefined;
    handlers.setStepIndex(nextIndex);
    persistSession(nextIndex, nextCompleted, nextStepId);
    stepStartedAtRef.current = Date.now();
  }, [
    completedIds,
    currentMissionStep,
    persistSession,
    state.currentFlowId,
    state.currentStepIndex,
    state.currentSteps,
    routeNavigating,
    routeReady,
    user?.id,
  ]);

  const skipOptionalStep = useCallback(() => {
    if (!currentMissionStep?.optional) {
      return;
    }
    trackEvent({
      action: ONBOARDING_EVENTS.LEARNING_LAB_MISSION_STEP_SKIPPED,
      category: 'onboarding',
      label: currentMissionStep.id,
      custom_parameters: { learning_lab_active: true },
    });
    advanceMissionStep();
  }, [advanceMissionStep, currentMissionStep]);

  const retryCurrentTarget = useCallback(() => {
    trackEvent({
      action: ONBOARDING_EVENTS.LEARNING_LAB_MISSION_SHOW_ME_RETRY,
      category: 'onboarding',
      label: currentMissionStep?.id ?? state.currentFlowId ?? 'unknown',
      custom_parameters: { learning_lab_active: true },
    });
    setPanelCollapsed(true);
    window.dispatchEvent(
      new CustomEvent(TOUR_DOM_EVENTS.MISSION_PANEL_COLLAPSE, {
        detail: { reason: 'show_me' },
      }),
    );
    window.setTimeout(() => {
      handlersRef.current?.refreshTarget();
      window.dispatchEvent(new CustomEvent(TOUR_DOM_EVENTS.MISSION_TARGET_PULSE));
    }, 350);
  }, [currentMissionStep?.id, state.currentFlowId]);

  const setTargetVisibility = useCallback(
    (visibility: 'ready' | 'missing' | 'occluded') => {
      setTargetVisibilityState(visibility);
      const occluded = visibility === 'occluded';
      setTargetOccluded(occluded);
      if (visibility === 'missing') {
        setPanelCollapsed(false);
      }
      if (occluded) {
        trackEvent({
          action: ONBOARDING_EVENTS.LEARNING_LAB_MISSION_TARGET_OCCLUDED,
          category: 'onboarding',
          label: currentMissionStep?.id ?? state.currentFlowId ?? 'unknown',
          custom_parameters: { learning_lab_active: true },
        });
      }
    },
    [currentMissionStep?.id, state.currentFlowId],
  );

  const targetReady = targetVisibility === 'ready';

  const showManualNext = useMemo(() => {
    if (!currentMissionStep || !routeReady || routeNavigating) {
      return false;
    }
    return shouldShowMissionManualNext(currentMissionStep.advanceGate, {
      targetReady,
      targetOccluded,
    });
  }, [
    currentMissionStep,
    routeNavigating,
    routeReady,
    targetOccluded,
    targetReady,
  ]);

  const fallbackAction = useMemo(() => {
    if (!currentMissionStep) {
      return null;
    }
    if (currentMissionStep.advanceGate.type === 'click') {
      return getMissionFallbackForAnchor(currentMissionStep.advanceGate.anchor);
    }
    if (targetVisibility === 'missing' && currentMissionStep.tourAnchor) {
      return getMissionFallbackForAnchor(currentMissionStep.tourAnchor);
    }
    return null;
  }, [currentMissionStep, targetVisibility]);

  const showFallbackAction = Boolean(
    fallbackAction &&
      fallbackAction.type !== 'none' &&
      (currentMissionStep?.advanceGate.type === 'click' ||
        targetVisibility === 'missing'),
  );

  const executeFallback = useCallback(() => {
    if (!fallbackAction) {
      return;
    }
    trackEvent({
      action: ONBOARDING_EVENTS.LEARNING_LAB_MISSION_FALLBACK_USED,
      category: 'onboarding',
      label: currentMissionStep?.id ?? 'unknown',
      custom_parameters: {
        learning_lab_active: true,
        fallback_type: fallbackAction.type,
      },
    });
    executeMissionFallback(fallbackAction);
    window.setTimeout(() => {
      handlersRef.current?.refreshTarget();
    }, 400);
  }, [currentMissionStep?.id, fallbackAction]);

  useEffect(() => {
    if (state.missionMode && state.isRunning) {
      document.documentElement.setAttribute('data-mission-active', 'true');
    } else {
      document.documentElement.removeAttribute('data-mission-active');
    }
    return () => {
      document.documentElement.removeAttribute('data-mission-active');
    };
  }, [state.isRunning, state.missionMode]);

  useEffect(() => {
    setPanelCollapsed(false);
    setTargetOccluded(false);
    setTargetVisibilityState('missing');
    setRouteNavigating(false);
  }, [state.currentStepIndex, state.currentFlowId]);

  useEffect(() => {
    if (
      !state.missionMode ||
      !state.isRunning ||
      !playbook ||
      !playbookStep ||
      routeReady
    ) {
      return undefined;
    }

    let cancelled = false;
    setRouteNavigating(true);
    void ensureMissionRouteForPlaybookStep(
      playbookStep,
      playbook.slug as CoordinatorLabTourSlug,
    ).finally(() => {
      if (!cancelled) {
        setRouteNavigating(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    playbook,
    playbookStep,
    routeReady,
    state.currentFlowId,
    state.currentStepIndex,
    state.isRunning,
    state.missionMode,
  ]);

  useEffect(() => {
    const onCollapse = () => {
      setPanelCollapsed(true);
      trackEvent({
        action: ONBOARDING_EVENTS.LEARNING_LAB_MISSION_PANEL_COLLAPSED,
        category: 'onboarding',
        label: currentMissionStep?.id ?? state.currentFlowId ?? 'unknown',
        custom_parameters: { learning_lab_active: true },
      });
    };
    const onExpand = () => setPanelCollapsed(false);
    window.addEventListener(TOUR_DOM_EVENTS.MISSION_PANEL_COLLAPSE, onCollapse);
    window.addEventListener(TOUR_DOM_EVENTS.MISSION_PANEL_EXPAND, onExpand);
    return () => {
      window.removeEventListener(TOUR_DOM_EVENTS.MISSION_PANEL_COLLAPSE, onCollapse);
      window.removeEventListener(TOUR_DOM_EVENTS.MISSION_PANEL_EXPAND, onExpand);
    };
  }, [currentMissionStep?.id, state.currentFlowId]);

  const resumeFromSession = useCallback((): number | null => {
    if (!user?.id || !state.currentFlowId) {
      return null;
    }
    const snapshot = loadMissionSession(user.id, state.currentFlowId);
    if (!snapshot) {
      return null;
    }
    setCompletedIds(new Set(snapshot.completedIds));
    stepStartedAtRef.current = snapshot.startedAt;
    return snapshot.stepIndex;
  }, [state.currentFlowId, user?.id]);

  useEffect(() => {
    gateCleanupRef.current?.();
    gateCleanupRef.current = null;

    if (!state.missionMode || !currentMissionStep) {
      return undefined;
    }

    if (currentMissionStep.advanceGate.type === 'manual') {
      return undefined;
    }

    const rowAnchor =
      routeContract?.rowAnchor ??
      (typeof currentMissionStep.metadata?.rowAnchor === 'string'
        ? currentMissionStep.metadata.rowAnchor
        : undefined);

    gateCleanupRef.current = subscribeAdvanceGate(
      currentMissionStep.advanceGate,
      () => {
        if (!handlersRef.current || !routeReady || routeNavigating) {
          return false;
        }
        advanceMissionStep();
        return true;
      },
      rowAnchor ? { rowAnchor } : undefined,
    );

    return () => {
      gateCleanupRef.current?.();
      gateCleanupRef.current = null;
    };
  }, [
    advanceMissionStep,
    currentMissionStep,
    routeContract?.rowAnchor,
    routeNavigating,
    routeReady,
    state.missionMode,
  ]);

  useEffect(() => {
    if (!state.missionMode) {
      hydratedMissionSlugRef.current = null;
      setCompletedIds(new Set());
      return;
    }
    if (!state.isRunning || !state.currentFlowId || !user?.id) {
      return;
    }
    if (hydratedMissionSlugRef.current === state.currentFlowId) {
      return;
    }
    hydratedMissionSlugRef.current = state.currentFlowId;
    const snapshot = loadMissionSession(user.id, state.currentFlowId);
    if (snapshot) {
      setCompletedIds(new Set(snapshot.completedIds));
      stepStartedAtRef.current = snapshot.startedAt;
      return;
    }
    if (state.missionCompletedStepIds?.length) {
      setCompletedIds(new Set(state.missionCompletedStepIds));
      return;
    }
    setCompletedIds(new Set());
  }, [
    state.currentFlowId,
    state.isRunning,
    state.missionCompletedStepIds,
    state.missionMode,
    user?.id,
  ]);

  useEffect(() => {
    if (!state.missionMode || !state.isRunning) {
      missionStartedSlugRef.current = null;
    }
  }, [state.isRunning, state.missionMode]);

  useEffect(() => {
    if (
      !state.missionMode ||
      !state.isRunning ||
      !playbook ||
      !user?.id ||
      missionStartedSlugRef.current === playbook.slug
    ) {
      return;
    }
    missionStartedSlugRef.current = playbook.slug;
    trackEvent({
      action: ONBOARDING_EVENTS.LEARNING_LAB_MISSION_STARTED,
      category: 'onboarding',
      label: playbook.slug,
      custom_parameters: { learning_lab_active: true },
    });
  }, [playbook, state.isRunning, state.missionMode, user?.id]);

  const checklistRows: ChecklistRow[] = useMemo(() => {
    if (!playbook) {
      return [];
    }
    const currentId = currentMissionStep?.id;
    return playbook.steps.map((step) => {
      let status: ChecklistRow['status'] = 'upcoming';
      if (completedIds.has(step.id)) {
        status = 'done';
      } else if (step.id === currentId) {
        status = 'current';
      }
      return {
        id: step.id,
        title: step.title,
        groupId: step.groupId,
        status,
      };
    });
  }, [completedIds, currentMissionStep?.id, playbook]);

  const value = useMemo(
    () => ({
      playbook,
      currentMissionStep,
      checklistRows,
      completedIds,
      panelCollapsed,
      targetOccluded,
      targetReady,
      showManualNext,
      routeReady,
      routeNavigating,
      showFallbackAction,
      fallbackLabel: fallbackAction?.label,
      setPanelCollapsed,
      setTargetVisibility,
      registerAdvanceHandlers,
      advanceMissionStep,
      skipOptionalStep,
      retryCurrentTarget,
      executeFallback,
      resumeFromSession,
    }),
    [
      advanceMissionStep,
      checklistRows,
      completedIds,
      currentMissionStep,
      executeFallback,
      fallbackAction?.label,
      panelCollapsed,
      playbook,
      registerAdvanceHandlers,
      resumeFromSession,
      retryCurrentTarget,
      routeNavigating,
      routeReady,
      showFallbackAction,
      showManualNext,
      skipOptionalStep,
      targetOccluded,
      targetReady,
    ],
  );

  return (
    <LearningLabMissionContext.Provider value={value}>
      {children}
    </LearningLabMissionContext.Provider>
  );
};

export default LearningLabMissionProvider;
